import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  FormLabel,
  Grid,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  addMedicationToProntuario,
  AddMedicationPayload,
  finishProntuarioMedication,
  getApiErrorMessage,
  getProntuarioMedications,
  Medication,
  ProntuarioMedication,
  ProntuarioMedicationStatus,
} from "src/apis/prontuario";
import { getToday, formatDateToBR } from "src/utils/date";
import { Id } from "src/utils/types";
import SkeletonLoad from "../SkeletonLoad";
import MedicationSearchCombobox from "./MedicationSearchCombobox";
import {
  formatEmpty,
  getMedicationActiveIngredient,
  getMedicationConcentration,
  getMedicationForm,
  getMedicationId,
  getMedicationImageUrls,
  getMedicationLinkId,
  getMedicationName,
  getMedicationPatientLeafletUrl,
  getMedicationProfessionalLeafletUrl,
  getMedicationRoute,
  medicationStatusLabels,
  getStatusColor,
} from "./utils";

type ProntuarioMedicationsProps = {
  prontuarioId: Id;
  onNotify: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
};

type MedicationFormValues = Omit<AddMedicationPayload, "medication_id">;

const defaultFormValues: MedicationFormValues = {
  dosage: "",
  frequency: "",
  usage_route: "",
  start_date: getToday(),
  end_date: null,
  status: "em_uso",
  notes: "",
  prescribed_by: "",
};

const hasLink = (value: string): boolean => value !== "-";

const ProntuarioMedications: FC<ProntuarioMedicationsProps> = ({
  prontuarioId,
  onNotify,
}) => {
  const [medications, setMedications] = useState<ProntuarioMedication[]>([]);
  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(null);
  const [formValues, setFormValues] = useState<MedicationFormValues>(defaultFormValues);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  const fetchMedications = useCallback(async () => {
    if (!prontuarioId) {
      return;
    }

    setLoading(true);

    try {
      const response = await getProntuarioMedications(prontuarioId);
      setMedications(response);
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível carregar medicamentos."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [prontuarioId, onNotify]);

  useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const handleChange = (
    field: keyof MedicationFormValues,
    value: string | ProntuarioMedicationStatus | null
  ) => {
    setFormValues((previousValues) => ({
      ...previousValues,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedMedication) {
      onNotify("Selecione um medicamento antes de salvar.", "warning");
      return;
    }

    const medicationId = getMedicationId(selectedMedication);

    if (!medicationId) {
      onNotify("Não foi possível identificar o medicamento selecionado.", "error");
      return;
    }

    setSaving(true);

    try {
      await addMedicationToProntuario(prontuarioId, {
        ...formValues,
        medication_id: medicationId,
        end_date: formValues.end_date || null,
      });
      setSelectedMedication(null);
      setFormValues(defaultFormValues);
      await fetchMedications();
      onNotify("Medicamento vinculado ao prontuário.", "success");
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível vincular o medicamento."),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFinishMedication = async (medication: ProntuarioMedication) => {
    const linkId = getMedicationLinkId(medication);

    if (!linkId) {
      onNotify("Não foi possível identificar o vínculo do medicamento.", "error");
      return;
    }

    if (!window.confirm("Deseja finalizar o uso deste medicamento no prontuário?")) {
      return;
    }

    setLoading(true);

    try {
      await finishProntuarioMedication(prontuarioId, linkId);
      await fetchMedications();
      onNotify("Uso do medicamento finalizado.", "success");
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível finalizar o medicamento."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MedicationSearchCombobox
              disabled={saving}
              onSelect={setSelectedMedication}
              onError={(message) => onNotify(message, "error")}
            />
          </Grid>

          {selectedMedication && (
            <>
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>{getMedicationName(selectedMedication)}</strong>
                  <br />
                  Princípio ativo: {getMedicationActiveIngredient(selectedMedication)}
                </Alert>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Dose</FormLabel>
                  <TextField
                    value={formValues.dosage}
                    onChange={(event) => handleChange("dosage", event.target.value)}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Frequência</FormLabel>
                  <TextField
                    value={formValues.frequency}
                    onChange={(event) => handleChange("frequency", event.target.value)}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Via de uso</FormLabel>
                  <TextField
                    value={formValues.usage_route}
                    onChange={(event) => handleChange("usage_route", event.target.value)}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Data de início</FormLabel>
                  <TextField
                    type="date"
                    value={formValues.start_date}
                    onChange={(event) => handleChange("start_date", event.target.value)}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Data de fim</FormLabel>
                  <TextField
                    type="date"
                    value={formValues.end_date ?? ""}
                    onChange={(event) => handleChange("end_date", event.target.value)}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormGroup>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formValues.status}
                    onChange={(event) =>
                      handleChange("status", event.target.value as ProntuarioMedicationStatus)
                    }
                  >
                    {Object.entries(medicationStatusLabels).map(([value, label]) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Prescrito por</FormLabel>
                  <TextField
                    value={formValues.prescribed_by}
                    onChange={(event) => handleChange("prescribed_by", event.target.value)}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Observações</FormLabel>
                  <TextField
                    value={formValues.notes}
                    onChange={(event) => handleChange("notes", event.target.value)}
                    multiline
                    minRows={2}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? undefined : <AddIcon />}
                  disabled={saving}
                >
                  {saving ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size="1.25rem" />
                      Salvando medicamento...
                    </Box>
                  ) : (
                    "Vincular medicamento"
                  )}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Medicamentos vinculados
        </Typography>

        {loading ? (
          <SkeletonLoad variant="table" rows={4} columns={8} />
        ) : medications.length === 0 ? (
          <Alert severity="info">Nenhum medicamento vinculado.</Alert>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Princípio ativo</strong></TableCell>
                  <TableCell><strong>Dose</strong></TableCell>
                  <TableCell><strong>Frequência</strong></TableCell>
                  <TableCell><strong>Via</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Início</strong></TableCell>
                  <TableCell><strong>Fim</strong></TableCell>
                  <TableCell><strong>Observações</strong></TableCell>
                  <TableCell><strong>Prescrito por</strong></TableCell>
                  <TableCell><strong>Bula</strong></TableCell>
                  <TableCell><strong>Ações</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medications.map((medication) => {
                  const patientLeafletUrl = getMedicationPatientLeafletUrl(medication);
                  const professionalLeafletUrl = getMedicationProfessionalLeafletUrl(medication);
                  const imageList = getMedicationImageUrls(medication);
                  const status = String(medication.status ?? "desconhecido");

                  return (
                    <TableRow key={getMedicationLinkId(medication)}>
                      <TableCell>
                        <Typography fontWeight={700}>
                          {getMedicationName(medication)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getMedicationConcentration(medication)} ·{" "}
                          {getMedicationForm(medication)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getMedicationActiveIngredient(medication)}</TableCell>
                      <TableCell>{formatEmpty(medication.dosage ?? medication.dose)}</TableCell>
                      <TableCell>
                        {formatEmpty(medication.frequency ?? medication.frequencia)}
                      </TableCell>
                      <TableCell>{getMedicationRoute(medication)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={getStatusColor(status)}
                          label={
                            medicationStatusLabels[
                              status as ProntuarioMedicationStatus
                            ] ?? formatEmpty(status)
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDateToBR(medication.start_date)}</TableCell>
                      <TableCell>{formatDateToBR(medication.end_date)}</TableCell>
                      <TableCell>{formatEmpty(medication.notes ?? medication.observacoes)}</TableCell>
                      <TableCell>{formatEmpty(medication.prescribed_by)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          {hasLink(patientLeafletUrl) && (
                            <Link href={patientLeafletUrl} target="_blank" rel="noreferrer">
                              Paciente
                            </Link>
                          )}
                          {hasLink(professionalLeafletUrl) && (
                            <Link href={professionalLeafletUrl} target="_blank" rel="noreferrer">
                              Profissional
                            </Link>
                          )}
                          {imageList.length > 0 && (
                            <Button
                              size="small"
                              startIcon={<ImageIcon />}
                              onClick={() => setImageUrls(imageList)}
                            >
                              Ver imagens da bula
                            </Button>
                          )}
                          {!hasLink(patientLeafletUrl) &&
                            !hasLink(professionalLeafletUrl) &&
                            imageList.length === 0 &&
                            "-"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {status !== "finalizado" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleFinishMedication(medication)}
                          >
                            Finalizar uso
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={imageUrls.length > 0} onClose={() => setImageUrls([])} maxWidth="md" fullWidth>
        <DialogTitle>Imagens da bula</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {imageUrls.map((url) => (
              <Box
                component="img"
                key={url}
                src={url}
                alt="Imagem da bula"
                sx={{ width: "100%", borderRadius: 1 }}
              />
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProntuarioMedications;
